<template>
  <transition name="settings-panel-slide">
    <div
      v-if="modelValue"
      class="settings-panel-overlay"
      role="button"
      tabindex="0"
      @click.self="handleClose"
      @keydown.escape="handleClose"
    >
      <div class="settings-panel">
        <div class="settings-panel__sidebar">
          <div class="settings-panel__sidebar-header">
            <div class="settings-panel__sidebar-title">Settings</div>
            <div class="settings-panel__sidebar-divider"></div>
          </div>

          <div class="settings-panel__nav">
            <v-btn
              variant="flat"
              size="small"
              rounded="pill"
              :class="['settings-panel__nav-btn', { 'settings-panel__nav-btn--active': activeTab === 'audio' }]"
              @click="activeTab = 'audio'"
            >
              <v-icon start size="24">mdi-microphone</v-icon>
              Audio
            </v-btn>
            <v-btn
              variant="outlined"
              size="small"
              rounded="pill"
              :class="['settings-panel__nav-btn', { 'settings-panel__nav-btn--active': activeTab === 'video' }]"
              @click="activeTab = 'video'"
            >
              <v-icon start size="24">mdi-video</v-icon>
              Video
            </v-btn>
          </div>
        </div>

        <div class="settings-panel__main">
          <div class="settings-panel__header">
            <h2 class="settings-panel__title">{{ activeTab === 'audio' ? 'Audio Settings' : 'Video Settings' }}</h2>
            <v-btn icon variant="text" size="small" class="settings-panel__close-btn" @click="handleClose">
              <v-icon size="20" color="#222222">mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="settings-panel__content">
            <!-- Audio Settings Tab -->
            <template v-if="activeTab === 'audio'">
              <div class="settings-panel__description">
                Check your audio before starting a call.<br />
                During calls, controls are managed in Zoom.
              </div>

              <div v-if="micPermissionDenied" class="settings-panel__info-alert">
                <div class="settings-panel__info-alert-icon">
                  <v-icon size="22" color="#E07B00">mdi-information</v-icon>
                </div>
                <span class="settings-panel__info-alert-text">Permissions denied</span>
              </div>

              <div v-if="!hasMicDevices && !micPermissionDenied" class="settings-panel__info-alert">
                <div class="settings-panel__info-alert-icon">
                  <v-icon size="22" color="#E07B00">mdi-information</v-icon>
                </div>
                <span class="settings-panel__info-alert-text">No Device Found</span>
              </div>

              <div class="settings-panel__input-group">
                <div class="settings-panel__input-label">Microphone input</div>
                <v-select
                  :model-value="microphoneDevice"
                  :items="microphoneDeviceItems"
                  item-title="label"
                  item-value="deviceId"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="micPermissionDenied || !hasMicDevices"
                  placeholder="No microphone detected"
                  class="settings-panel__select"
                  @update:model-value="handleMicrophoneChange"
                >
                  <template #prepend-inner>
                    <v-icon size="24" color="#222222">mdi-microphone-outline</v-icon>
                  </template>
                </v-select>
                <v-btn
                  variant="flat"
                  size="small"
                  rounded="pill"
                  class="settings-panel__test-btn"
                  :disabled="micPermissionDenied || !hasMicDevices"
                  @click="handleTestAudio"
                >
                  {{ isTesting ? 'Stop Test' : 'Test Audio' }}
                </v-btn>
              </div>

              <div class="settings-panel__test-section">
                <div class="settings-panel__input-label">Microphone test</div>
                <div class="settings-panel__test-status">
                  <span v-if="!isTesting" class="settings-panel__test-placeholder">No input</span>
                  <div v-else class="settings-panel__level-meter">
                    <div
                      class="settings-panel__level-bar"
                      :style="{ width: `${micLevel}%` }"
                    ></div>
                  </div>
                </div>
              </div>

              <div class="settings-panel__input-group">
                <div class="settings-panel__input-label">Speaker output</div>
                <v-select
                  :model-value="speakerDevice"
                  :items="speakerDeviceItems"
                  item-title="label"
                  item-value="deviceId"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="!hasSpeakerDevices"
                  placeholder="No speaker detected"
                  class="settings-panel__select"
                  @update:model-value="handleSpeakerChange"
                >
                  <template #prepend-inner>
                    <v-icon size="24" color="#222222">mdi-volume-high</v-icon>
                  </template>
                </v-select>
                <v-btn
                  variant="flat"
                  size="small"
                  rounded="pill"
                  class="settings-panel__test-btn"
                  :disabled="!hasSpeakerDevices || isTestingSpeaker"
                  @click="handleTestSpeaker"
                >
                  {{ isTestingSpeaker ? 'Playing...' : 'Test Speaker' }}
                </v-btn>
              </div>
            </template>

            <!-- Video Settings Tab -->
            <template v-else-if="activeTab === 'video'">
              <div class="settings-panel__description">
                Check your video before starting a call.<br />
                During calls, controls are managed in Zoom.
              </div>

              <div v-if="cameraPermissionDenied" class="settings-panel__info-alert">
                <div class="settings-panel__info-alert-icon">
                  <v-icon size="22" color="#E07B00">mdi-information</v-icon>
                </div>
                <span class="settings-panel__info-alert-text">Permissions denied</span>
              </div>

              <div v-if="!hasCameraDevices && !cameraPermissionDenied" class="settings-panel__info-alert">
                <div class="settings-panel__info-alert-icon">
                  <v-icon size="22" color="#E07B00">mdi-information</v-icon>
                </div>
                <span class="settings-panel__info-alert-text">No Device Found</span>
              </div>

              <div class="settings-panel__video-preview">
                <div class="settings-panel__video-placeholder" :class="{ 'settings-panel__video-placeholder--active': cameraEnabled && videoStream }">
                  <video
                    v-if="cameraEnabled && videoStream"
                    ref="videoPreviewEl"
                    class="settings-panel__video-element"
                    autoplay
                    muted
                    playsinline
                  ></video>
                  <v-icon v-else size="48" color="rgba(255,255,255,0.3)">mdi-video-off</v-icon>
                  <div v-if="cameraEnabled && videoStream" class="settings-panel__video-status">Camera active</div>
                </div>
              </div>

              <div class="settings-panel__toggle-group">
                <div class="settings-panel__toggle-header">
                  <div class="settings-panel__input-label">Camera</div>
                  <div class="settings-panel__toggle-description">Toggle camera on or off</div>
                </div>
                <v-switch
                  v-model="cameraEnabled"
                  color="#795DE9"
                  hide-details
                  density="compact"
                  class="settings-panel__switch"
                  @update:model-value="handleCameraToggle"
                >
                  <template #label>
                    <span class="settings-panel__switch-label">{{ cameraEnabled ? 'On' : 'Off' }}</span>
                  </template>
                </v-switch>
              </div>

              <div class="settings-panel__input-group">
                <div class="settings-panel__input-label">Camera Device</div>
                <v-select
                  :model-value="cameraDevice"
                  :items="cameraDeviceItems"
                  item-title="label"
                  item-value="deviceId"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="!hasCameraDevices || !cameraEnabled"
                  placeholder="No camera detected"
                  class="settings-panel__select"
                  @update:model-value="handleCameraChange"
                >
                  <template #prepend-inner>
                    <v-icon size="24" color="#222222">mdi-video-plus-outline</v-icon>
                  </template>
                </v-select>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'

interface MediaDeviceInfo {
  deviceId: string
  label: string
  kind: string
}

interface Props {
  modelValue: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const activeTab = ref<'audio' | 'video'>('audio')

// -- Device lists --
const microphoneDevices = ref<MediaDeviceInfo[]>([])
const speakerDevices = ref<MediaDeviceInfo[]>([])
const cameraDevices = ref<MediaDeviceInfo[]>([])

// -- Selected devices --
const microphoneDevice = ref<string>('')
const speakerDevice = ref<string>('')
const cameraDevice = ref<string>('')

// -- Permission state --
const micPermissionDenied = ref(false)
const cameraPermissionDenied = ref(false)

// -- Camera toggle --
const cameraEnabled = ref(true)
const videoStream = ref<MediaStream | null>(null)
const videoPreviewEl = ref<HTMLVideoElement | null>(null)

// -- Mic testing --
const isTesting = ref(false)
const micLevel = ref(0)
let audioContext: AudioContext | null = null
let analyserNode: AnalyserNode | null = null
let micStream: MediaStream | null = null
let animFrameId: number | null = null

// -- Speaker testing --
const isTestingSpeaker = ref(false)
let speakerAudio: HTMLAudioElement | null = null

// -- Computed helpers --
const microphoneDeviceItems = computed(() =>
  microphoneDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Microphone ${i + 1}`,
  }))
)

const speakerDeviceItems = computed(() =>
  speakerDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Speaker ${i + 1}`,
  }))
)

const cameraDeviceItems = computed(() =>
  cameraDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Camera ${i + 1}`,
  }))
)

const hasMicDevices = computed(() => microphoneDevices.value.length > 0)
const hasSpeakerDevices = computed(() => speakerDevices.value.length > 0)
const hasCameraDevices = computed(() => cameraDevices.value.length > 0)

// -- Device enumeration --
async function enumerateDevices() {
  try {
    // Request permission to get labelled device names
    await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
      s.getTracks().forEach((t) => t.stop())
    }).catch(() => {
      micPermissionDenied.value = true
    })

    const devices = await navigator.mediaDevices.enumerateDevices()

    microphoneDevices.value = devices
      .filter((d) => d.kind === 'audioinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    speakerDevices.value = devices
      .filter((d) => d.kind === 'audiooutput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    cameraDevices.value = devices
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    if (hasMicDevices.value && !microphoneDevice.value) {
      microphoneDevice.value = microphoneDevices.value[0].deviceId
    }

    if (hasSpeakerDevices.value && !speakerDevice.value) {
      speakerDevice.value = speakerDevices.value[0].deviceId
    }

    if (hasCameraDevices.value && !cameraDevice.value) {
      cameraDevice.value = cameraDevices.value[0].deviceId
    }
  } catch {
    micPermissionDenied.value = true
  }
}

// -- Camera preview --
async function startCameraPreview(deviceId?: string) {
  stopCameraPreview()
  if (cameraPermissionDenied.value) return

  try {
    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    videoStream.value = stream
    await nextTick()
    if (videoPreviewEl.value) {
      videoPreviewEl.value.srcObject = stream
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      cameraPermissionDenied.value = true
    }
    videoStream.value = null
  }
}

function stopCameraPreview() {
  if (videoStream.value) {
    videoStream.value.getTracks().forEach((t) => t.stop())
    videoStream.value = null
  }
}

// -- Microphone test --
async function handleTestAudio() {
  if (isTesting.value) {
    stopMicTest()
    return
  }

  try {
    const constraints: MediaStreamConstraints = {
      audio: microphoneDevice.value ? { deviceId: { exact: microphoneDevice.value } } : true,
    }
    micStream = await navigator.mediaDevices.getUserMedia(constraints)
    audioContext = new AudioContext()
    const source = audioContext.createMediaStreamSource(micStream)
    analyserNode = audioContext.createAnalyser()
    analyserNode.fftSize = 256
    source.connect(analyserNode)

    isTesting.value = true
    animateMicLevel()
  } catch {
    micPermissionDenied.value = true
  }
}

function animateMicLevel() {
  if (!analyserNode || !isTesting.value) return

  const dataArray = new Uint8Array(analyserNode.frequencyBinCount)
  analyserNode.getByteTimeDomainData(dataArray)

  let sum = 0
  for (let i = 0; i < dataArray.length; i++) {
    const val = (dataArray[i] - 128) / 128
    sum += val * val
  }
  const rms = Math.sqrt(sum / dataArray.length)
  micLevel.value = Math.min(100, Math.round(rms * 400))

  animFrameId = requestAnimationFrame(animateMicLevel)
}

function stopMicTest() {
  isTesting.value = false
  micLevel.value = 0

  if (animFrameId !== null) {
    cancelAnimationFrame(animFrameId)
    animFrameId = null
  }
  if (micStream) {
    micStream.getTracks().forEach((t) => t.stop())
    micStream = null
  }
  if (audioContext) {
    audioContext.close()
    audioContext = null
  }
  analyserNode = null
}

// -- Speaker test --
function handleTestSpeaker() {
  if (isTestingSpeaker.value) return

  isTestingSpeaker.value = true
  speakerAudio = new Audio('/test-tone.mp3')

  // Fallback: generate a short beep via Web Audio API if no file
  const ctx = new AudioContext()
  const oscillator = ctx.createOscillator()
  const gainNode = ctx.createGain()
  oscillator.connect(gainNode)
  gainNode.connect(ctx.destination)
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(440, ctx.currentTime)
  gainNode.gain.setValueAtTime(0.3, ctx.currentTime)
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5)
  oscillator.start(ctx.currentTime)
  oscillator.stop(ctx.currentTime + 1.5)

  oscillator.onended = () => {
    ctx.close()
    isTestingSpeaker.value = false
  }
}

// -- Event handlers --
const handleMicrophoneChange = (value: string) => {
  microphoneDevice.value = value
  if (isTesting.value) {
    stopMicTest()
  }
}

const handleSpeakerChange = (value: string) => {
  speakerDevice.value = value
}

const handleCameraChange = (value: string) => {
  cameraDevice.value = value
  if (cameraEnabled.value) {
    startCameraPreview(value)
  }
}

const handleCameraToggle = (enabled: boolean | null) => {
  if (enabled) {
    startCameraPreview(cameraDevice.value || undefined)
  } else {
    stopCameraPreview()
  }
}

const handleClose = () => {
  stopMicTest()
  stopCameraPreview()
  emit('update:modelValue', false)
  emit('close')
}

// -- Lifecycle watchers --
watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      await enumerateDevices()
      if (activeTab.value === 'video') {
        await startCameraPreview(cameraDevice.value || undefined)
      }
    } else {
      stopMicTest()
      stopCameraPreview()
    }
  }
)

watch(activeTab, async (tab) => {
  if (tab === 'video') {
    if (!hasCameraDevices.value) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true }).then((s) => s.getTracks().forEach((t) => t.stop()))
        const devices = await navigator.mediaDevices.enumerateDevices()
        cameraDevices.value = devices
          .filter((d) => d.kind === 'videoinput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))
        if (hasCameraDevices.value && !cameraDevice.value) {
          cameraDevice.value = cameraDevices.value[0].deviceId
        }
      } catch {
        cameraPermissionDenied.value = true
      }
    }
    if (cameraEnabled.value) {
      await startCameraPreview(cameraDevice.value || undefined)
    }
  } else {
    stopCameraPreview()
    stopMicTest()
  }
})

onUnmounted(() => {
  stopMicTest()
  stopCameraPreview()
})
</script>

<style lang="scss" scoped>
.settings-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(246, 245, 251, 0.7);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.settings-panel {
  width: 45%;
  height: 100vh;
  background: #ffffff;
  box-shadow: 0px 9px 19px 0px rgba(19, 19, 19, 0.1), 0px 35px 35px 0px rgba(19, 19, 19, 0.09),
    0px 79px 47px 0px rgba(19, 19, 19, 0.05), 0px 140px 56px 0px rgba(19, 19, 19, 0.01);
  border-radius: 10px 0 0 10px;
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.settings-panel__sidebar {
  width: 30%;
  height: 100%;
  background: #ffffff;
  border-right: 1px solid #d4d1de;
  display: flex;
  flex-direction: column;
  padding: 32px 24px 24px;
}

.settings-panel__sidebar-header {
  margin-bottom: 24px;
}

.settings-panel__sidebar-title {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5em;
  color: #222222;
  margin-bottom: 12px;
}

.settings-panel__sidebar-divider {
  height: 1px;
  background: #d4d1de;
  opacity: 0.2;
}

.settings-panel__nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-panel__nav-btn {
  justify-content: flex-start !important;
  text-transform: none;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0;
  padding: 8px 16px !important;
  height: auto !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;

  &--active {
    background-color: #795de9 !important;
    color: #ffffff !important;

    :deep(.v-icon) {
      color: #ffffff !important;
    }
  }

  &:not(&--active) {
    background-color: #ffffff !important;
    color: #222222 !important;
    border: 1.5px solid #d4d1de !important;

    :deep(.v-icon) {
      color: #222222 !important;
    }

    &:hover {
      background-color: #f6f5fb !important;
    }
  }
}

.settings-panel__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.settings-panel__header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 32px 32px 24px;
  background: #ffffff;
}

.settings-panel__title {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5em;
  color: #222222;
  margin: 0;
}

.settings-panel__close-btn {
  background: transparent;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}

.settings-panel__content {
  flex: 1;
  padding: 0 32px 32px;
  overflow-y: auto;

  & > * {
    width: 100%;
    margin-bottom: 24px;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(106, 93, 138, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(106, 93, 138, 0.5);
    }
  }
}

.settings-panel__description {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5em;
  color: #6a5d8a;
}

// Custom info-style alert matching the screenshot (amber info-circle on beige background)
.settings-panel__info-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fdf6ec;
  border-radius: 10px;
  padding: 14px 16px;
}

.settings-panel__info-alert-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #E07B00;
}

.settings-panel__info-alert-text {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.5em;
  color: #222222;
}

.settings-panel__input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-panel__input-label {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5em;
  color: #222222;
}

.settings-panel__select {
  font-family: 'Proxima Nova', sans-serif;

  :deep(.v-field) {
    border: 1px solid #d4d1de;
    border-radius: 12px;
    background: #ffffff;
  }

  :deep(.v-field__input) {
    font-family: 'Proxima Nova', sans-serif;
    font-size: 16px;
    line-height: 1.5em;
    padding: 12px 16px;
    min-height: 48px;
  }

  :deep(.v-field--focused) {
    border-color: #291758;
  }

  :deep(.v-field__prepend-inner) {
    padding-left: 12px;
    padding-right: 8px;
  }
}

.settings-panel__test-btn {
  background-color: #e3ddf3 !important;
  color: #222222 !important;
  text-transform: none;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0;
  box-shadow: none !important;
  align-self: flex-start;

  &:disabled {
    background-color: #f0f0f0 !important;
    color: #999999 !important;
  }
}

.settings-panel__test-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-panel__test-status {
  font-family: 'Proxima Nova', sans-serif;
  min-height: 48px;
  padding: 12px 16px;
  background: #f6f5fb;
  border-radius: 8px;
  border: 1px solid #d4d1de;
  display: flex;
  align-items: center;
}

.settings-panel__test-placeholder {
  font-size: 14px;
  line-height: 1.5em;
  color: #9e93b8;
}

.settings-panel__level-meter {
  flex: 1;
  height: 10px;
  background: #e0daef;
  border-radius: 5px;
  overflow: hidden;
}

.settings-panel__level-bar {
  height: 100%;
  background: #795de9;
  border-radius: 5px;
  transition: width 0.08s ease-out;
}

.settings-panel__video-preview {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.settings-panel__video-placeholder {
  width: 100%;
  height: 240px;
  background: #1a1a1a;
  border-radius: 12px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &--active {
    align-items: flex-end;
    justify-content: flex-start;
    padding: 16px;
  }
}

.settings-panel__video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.settings-panel__video-status {
  position: relative;
  z-index: 1;
  background: #f6f5fb;
  padding: 8px 16px;
  border-radius: 20px;
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #222222;
}

.settings-panel__toggle-group {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.settings-panel__toggle-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-panel__toggle-description {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5em;
  color: #6a5d8a;
}

.settings-panel__switch {
  :deep(.v-switch__track) {
    width: 48px;
    height: 24px;
    opacity: 1;
  }

  :deep(.v-switch__thumb) {
    width: 20px;
    height: 20px;
  }

  :deep(.v-selection-control) {
    min-height: unset;
  }

  :deep(.v-label) {
    margin-left: 12px;
    font-family: 'Proxima Nova', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: #222222;
  }
}

.settings-panel__switch-label {
  font-family: 'Proxima Nova', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #222222;
}

.settings-panel-slide-enter-active,
.settings-panel-slide-leave-active {
  transition: all 0.3s ease;

  .settings-panel {
    transition: transform 0.3s ease;
  }
}

.settings-panel-slide-enter-from {
  opacity: 0;

  .settings-panel {
    transform: translateX(100%);
  }
}

.settings-panel-slide-leave-to {
  opacity: 0;

  .settings-panel {
    transform: translateX(100%);
  }
}
</style>
